// Search-as-you-type pickers for choosing a company, contact or building.

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { cx, Input } from './ui';

interface Option {
  id: string;
  label: string;
  detail?: string | null;
}

function Picker({
  placeholder,
  search,
  onPick,
  queryKey,
  onCreate,
  createLabel = 'Create new',
}: {
  placeholder: string;
  search: (q: string) => Promise<Option[]>;
  onPick: (o: Option) => void;
  queryKey: string;
  /** When set, offers to create a new record from what was typed. */
  onCreate?: (typed: string) => void;
  createLabel?: string;
}) {
  const [q, setQ] = useState('');
  const { data = [], isFetching } = useQuery({
    queryKey: ['picker', queryKey, q],
    queryFn: () => search(q.trim()),
    enabled: q.trim().length >= 2,
  });
  return (
    <div>
      <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} />
      {q.trim().length >= 2 && (
        <ul className="mt-1 max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-white text-sm shadow-sm">
          {data.map((o) => (
            <li key={o.id}>
              <button type="button" className="w-full px-3 py-1.5 text-left hover:bg-indigo-50" onClick={() => onPick(o)}>
                <span className="font-medium text-slate-800">{o.label}</span>
                {o.detail && <span className="ml-2 text-xs text-slate-500">{o.detail}</span>}
              </button>
            </li>
          ))}
          {!isFetching && data.length === 0 && (
            <li className={cx('px-3 py-2 text-slate-400')}>
              No matches
              {onCreate && (
                <>
                  {' – '}
                  <button type="button" className="font-medium text-indigo-600 hover:underline" onClick={() => onCreate(q.trim())}>
                    {createLabel}?
                  </button>
                </>
              )}
            </li>
          )}
          {onCreate && data.length > 0 && (
            <li className="border-t border-slate-100">
              <button type="button" className="w-full px-3 py-1.5 text-left text-indigo-600 hover:bg-indigo-50" onClick={() => onCreate(q.trim())}>
                + {createLabel} “{q.trim()}”
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

/** Escapes % and _ for ilike and commas/parens for PostgREST or() filters. */
function like(q: string): string {
  return `%${q.replace(/[%_\\]/g, (c) => `\\${c}`).replace(/[,()]/g, ' ')}%`;
}

export function OrgPicker({ onPick }: { onPick: (o: Option) => void }) {
  return (
    <Picker
      queryKey="org"
      placeholder="Type a company name…"
      onPick={onPick}
      search={async (q) => {
        const { data, error } = await supabase.from('organizations').select('id, name, org_type, website_domain').ilike('name', like(q)).order('name').limit(20);
        if (error) throw error;
        return data.map((o) => ({ id: o.id, label: o.name, detail: o.website_domain }));
      }}
    />
  );
}

export function ContactPicker({ onPick, onCreate }: { onPick: (o: Option) => void; onCreate?: (typed: string) => void }) {
  return (
    <Picker
      onCreate={onCreate}
      createLabel="Create new contact"
      queryKey="contact"
      placeholder="Type a person's name…"
      onPick={onPick}
      search={async (q) => {
        const p = like(q);
        const { data, error } = await supabase
          .from('contacts')
          .select('id, first_name, last_name, display_name, organizations(name)')
          .or(`first_name.ilike.${p},last_name.ilike.${p},display_name.ilike.${p}`)
          .order('last_name')
          .limit(20);
        if (error) throw error;
        return data.map((c) => ({
          id: c.id,
          label: [c.first_name, c.last_name].filter(Boolean).join(' ') || c.display_name || '(no name)',
          detail: c.organizations?.name,
        }));
      }}
    />
  );
}

export function BuildingPicker({ onPick, onCreate }: { onPick: (o: Option) => void; onCreate?: (typed: string) => void }) {
  return (
    <Picker
      onCreate={onCreate}
      createLabel="Create new building"
      queryKey="building"
      placeholder="Type a street or building name…"
      onPick={onPick}
      search={async (q) => {
        const p = like(q);
        const { data, error } = await supabase
          .from('buildings')
          .select('id, name, street, city')
          .or(`street.ilike.${p},name.ilike.${p},address_raw.ilike.${p}`)
          .order('street')
          .limit(20);
        if (error) throw error;
        return data.map((b) => ({ id: b.id, label: b.name || b.street || '(no address)', detail: [b.street && b.name ? b.street : null, b.city].filter(Boolean).join(', ') }));
      }}
    />
  );
}
